import { useCallback } from 'react';
import { MarkerProperties } from '@alltrails/types/CustomRoute';
import { useAppDispatch, useAppSelector } from '../../../redux';
import {
  getMarkerSelectionProperties,
  selectCustomRouteActiveSegment,
  updateActiveSegmentHandle,
  updateEndPointOfPreviousLinkedSegment,
  updateStartPointOfNextLinkedSegment
} from '../../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../../redux/customRoutesApi';
import { LatLng } from '../../../types/Geo';
import { isLinkedToNextSegment, isLinkedToPreviousSegment } from '../../../utils/customRoutes/segmentLinking';
import useRouting from '../useRouting';

const useRerouteMarker = () => {
  const dispatch = useAppDispatch();
  const { activeSegmentIndex, segments, selectedHandleIndex } = useAppSelector(state => ({
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    segments: state.customRoute.route.segments,
    selectedHandleIndex: state.customRoute.selectedHandleIndex
  }));
  const activeSegment = useAppSelector(selectCustomRouteActiveSegment);

  const routing = useRouting();

  const getPreviousLinkedSegmentRoute = useCallback(
    (marker: MarkerProperties) => {
      const { latitude, longitude } = marker;

      if (isLinkedToPreviousSegment(segments, activeSegmentIndex)) {
        const previousSegment = segments[activeSegmentIndex! - 1];
        const lastPath = previousSegment.paths[previousSegment.paths.length - 1];
        if (!lastPath || lastPath.points.length === 0) {
          return undefined;
        }

        const points: LatLng[] = [lastPath.points[0], [latitude, longitude]];
        return transformPointsToServerRoutes(points)[0];
      }

      return undefined;
    },
    [activeSegmentIndex, segments]
  );

  const getNextLinkedSegmentRoute = useCallback(
    (marker: MarkerProperties) => {
      const { latitude, longitude } = marker;

      if (isLinkedToNextSegment(segments, activeSegmentIndex)) {
        const nextSegment = segments[activeSegmentIndex! + 1];
        const firstPath = nextSegment.paths[0];
        if (!firstPath || firstPath.points.length === 0) {
          return undefined;
        }

        const points: LatLng[] = [[latitude, longitude], firstPath.points[firstPath.points.length - 1]];
        return transformPointsToServerRoutes(points)[0];
      }

      return undefined;
    },
    [activeSegmentIndex, segments]
  );

  const rerouteMarker = useCallback(
    async (marker: MarkerProperties) => {
      if (!activeSegment) {
        return;
      }

      const { latitude, longitude } = marker;
      const { isStart, isEnd, pathIndex } = getMarkerSelectionProperties(marker, selectedHandleIndex);

      let points;

      // There is some repetition in this if statement but it is structured to explicitly map marker type to behavior
      if (isStart) {
        const path = activeSegment.paths[0];
        points = [[latitude, longitude], path.points[path.points.length - 1]];
      } else if (isEnd) {
        const path = activeSegment.paths[activeSegment.paths.length - 1];
        points = [path.points[0], [latitude, longitude]];
      } else {
        const prevPath = activeSegment.paths[pathIndex - 1];
        const path = activeSegment.paths[pathIndex];
        points = [prevPath.points[0], [latitude, longitude], path.points[path.points.length - 1]];
      }

      const routes = [transformPointsToServerRoutes(points)[0]];

      if (isStart) {
        const previousLinkedSegmentRoute = getPreviousLinkedSegmentRoute(marker);
        if (previousLinkedSegmentRoute) {
          routes.push(previousLinkedSegmentRoute);
        }
      } else if (isEnd) {
        const nextLinkedSegmentRoute = getNextLinkedSegmentRoute(marker);
        if (nextLinkedSegmentRoute) {
          routes.push(nextLinkedSegmentRoute);
        }
      }

      const { isSuccess, data } = await routing({ routes });
      if (!isSuccess || !data?.groupedSegments || data.groupedSegments.length !== routes.length) {
        return;
      }

      const newPaths = data.groupedSegments[0].pathGroups?.[0]?.paths;

      if (!newPaths?.length) {
        return;
      }

      let beforePoints: LatLng[] | undefined;
      let afterPoints: LatLng[] | undefined;

      if (!isStart) {
        beforePoints = newPaths[0].points;
      }

      if (isStart) {
        afterPoints = newPaths[0].points;
      } else if (!isEnd) {
        afterPoints = newPaths[1].points;
      }

      // TODO: Need to combine this into a single action to avoid multiple undo/redo states
      dispatch(updateActiveSegmentHandle({ ...marker, beforePoints, afterPoints }));

      if (routes.length === 2) {
        const newSegmentPaths = data.groupedSegments[1].pathGroups?.[0]?.paths;
        if (Boolean(newSegmentPaths?.length)) {
          if (isStart) {
            // Don't update the undo/redo stacks since updateActiveSegmentHandle already does
            dispatch(updateEndPointOfPreviousLinkedSegment({ addToUndoRedoStacks: false, latitude, longitude, points: newSegmentPaths[0].points }));
          } else {
            // Don't update the undo/redo stacks since updateActiveSegmentHandle already does
            dispatch(updateStartPointOfNextLinkedSegment({ addToUndoRedoStacks: false, latitude, longitude, points: newSegmentPaths[0].points }));
          }
        }
      }
    },
    [activeSegment, dispatch, getNextLinkedSegmentRoute, getPreviousLinkedSegmentRoute, routing, selectedHandleIndex]
  );

  return rerouteMarker;
};

export default useRerouteMarker;
